// 首页区块标准化
import { resolveFileUrl } from '@/components/shared/utils/fileUrl'
import { normalizeCertificationList } from '@/components/modules/products/utils/productNormalize'
import { resolveItemLink, resolveProductLink } from '@/components/home/utils/homeSectionItemLink'
import { resolveTemplate } from '@/components/home/utils/homeTemplates'
import { extractFirstContentImage } from '@/components/home/utils/extractFirstContentImage'
import { findRawMenuNodeById } from '@/components/layout/utils/navMenu'
import { findMenuPathByMenuId, slugFromMenuPath } from '@/components/portal/common/utils/menuPath'
import { resolveHomeSectionHeadingFields } from '@/components/home/utils/homeSectionHeadingStyle'

/** 菜单是否可作为焦点轮播 Tab（关闭的不要，顶栏隐藏的仍保留） */
function isFocusCarouselTabMenuEnabled(node) {
  return Boolean(node) && node.enabled !== false
}

/** 递归收集菜单节点及其子节点的所有 menuId */
function collectMenuIdsUnderNode(menuNode) {
  const ids = new Set()
  if (!menuNode) return ids

  // walk
  const walk = (node) => {
    if (!isFocusCarouselTabMenuEnabled(node)) return
    ids.add(String(node.id))
    for (const child of node.children || []) {
      walk(child)
    }
  }

  walk(menuNode)
  return ids
}

/** 判断 menuId 是否为 ancestorId 的自身或后代节点 */
function isDescendantOrSelf(menus, ancestorId, menuId) {
  let current = findRawMenuNodeById(menus, menuId)
  const target = String(ancestorId)

  while (current) {
    if (String(current.id) === target) return true
    current = current.parentId != null
      ? findRawMenuNodeById(menus, current.parentId)
      : null
  }

  return false
}

/** 子菜单内容类型：未填写时沿用父级 */
function resolveFocusCarouselChildType(child, parentType, fallbackType) {
  return child?.contentType || parentType || fallbackType || ''
}

/** 获取焦点轮播区块的 Tab 子菜单（按 CMS contentType 过滤） */
function getFocusCarouselTabChildren(menuNode, contentType = 'NEWS') {
  if (!menuNode?.children?.length) return []

  const parentType = menuNode.contentType || contentType

  return (menuNode.children || [])
    .filter((child) => isFocusCarouselTabMenuEnabled(child))
    .filter((child) => {
      if (!contentType) return true
      return resolveFocusCarouselChildType(child, parentType, contentType) === contentType
    })
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
}

/** 从内容条目推断焦点轮播关联的父级 menuId */
function inferFocusCarouselMenuId(menus, rawItems = [], contentType = 'NEWS') {
  const menuIds = [...new Set(
    (rawItems || [])
      .map((item) => item?.menuId)
      .filter((value) => value != null && value !== '')
  )]

  if (!menuIds.length) return null
  if (menuIds.length === 1) return menuIds[0]

  let candidate = findRawMenuNodeById(menus, menuIds[0])
  while (candidate) {
    const allUnderCandidate = menuIds.every((menuId) => isDescendantOrSelf(menus, candidate.id, menuId))
    if (allUnderCandidate && getFocusCarouselTabChildren(candidate, contentType).length > 1) {
      return candidate.id
    }
    candidate = candidate.parentId != null
      ? findRawMenuNodeById(menus, candidate.parentId)
      : null
  }

  return menuIds[0]
}

/** 发布时间从新到旧；空日期靠后 */
function comparePublishedAtDesc(a, b) {
  const left = String(a?.publishedAt || a?.createdAt || '')
  const right = String(b?.publishedAt || b?.createdAt || '')
  if (left === right) return 0
  if (!left) return 1
  if (!right) return -1
  return right.localeCompare(left)
}

function sortItemsByPublishedAtDesc(items = []) {
  return [...(items || [])].sort(comparePublishedAtDesc)
}

/** 从菜单子节点生成有条目的 Tab */
function buildTabsFromMenuChildren(children, items, displayCount) {
  const limit = displayCount > 0 ? displayCount : items.length

  return children.map((child) => {
    const allowedIds = collectMenuIdsUnderNode(child)
    const tabItems = sortItemsByPublishedAtDesc(
      items.filter((item) => allowedIds.has(String(item.menuId)))
    ).slice(0, limit)

    return {
      key: String(child.id),
      label: child.name,
      menuId: child.id,
      items: tabItems
    }
  }).filter((tab) => tab.items.length)
}

/** 按已选条目的 menuId 分组生成 Tab（菜单树凑不出切换栏时的兜底） */
function buildTabsFromItems(menus, items, displayCount) {
  const limit = displayCount > 0 ? displayCount : items.length
  const groups = new Map()

  for (const item of items) {
    if (item?.menuId == null || item.menuId === '') continue
    const key = String(item.menuId)
    if (!groups.has(key)) {
      const node = findRawMenuNodeById(menus, key)
      groups.set(key, {
        key,
        label: node?.name || item.menuName || '',
        menuId: item.menuId,
        items: []
      })
    }
    groups.get(key).items.push(item)
  }

  return [...groups.values()]
    .map((tab) => ({
      ...tab,
      items: sortItemsByPublishedAtDesc(tab.items).slice(0, limit)
    }))
    .filter((tab) => tab.items.length)
}

/** 规范化首页模块配置的标签菜单顺序 */
function normalizeTabMenuIds(value) {
  if (!Array.isArray(value)) return []
  const seen = new Set()
  const ids = []
  for (const raw of value) {
    if (raw == null || raw === '') continue
    const key = String(raw)
    if (seen.has(key)) continue
    seen.add(key)
    ids.push(raw)
  }
  return ids
}

/** 按 templateConfig.tabMenuIds 重排；未出现的标签接在后面 */
function applyTabMenuOrder(tabs, tabMenuIds) {
  if (!Array.isArray(tabs) || tabs.length <= 1) return tabs || []
  const order = normalizeTabMenuIds(tabMenuIds).map(String)
  if (!order.length) return tabs
  const rank = new Map(order.map((id, index) => [id, index]))
  const keyed = tabs.map((tab, index) => ({
    tab,
    index,
    key: String(tab.menuId ?? tab.key ?? '')
  }))
  const known = keyed
    .filter((item) => rank.has(item.key))
    .sort((a, b) => rank.get(a.key) - rank.get(b.key) || a.index - b.index)
  const unknown = keyed.filter((item) => !rank.has(item.key))
  return [...known, ...unknown].map((item) => item.tab)
}

/** 条目所属标签（自身或落在某标签子树下） */
function resolveItemTabMenuId(item, tabs = [], menus = []) {
  const menuId = item?.menuId
  if (menuId == null || menuId === '') return null
  const key = String(menuId)
  for (const tab of tabs) {
    const tabId = tab?.menuId ?? tab?.key
    if (tabId == null || tabId === '') continue
    if (String(tabId) === key) return tabId
    const node = findRawMenuNodeById(menus, tabId)
    if (collectMenuIdsUnderNode(node).has(key)) return tabId
  }
  return menuId
}

/** 按 itemGroupIds 或 sortOrder 排出用于推标签顺序的条目，不依赖接口 items 原顺序 */
function orderItemsForTabSequence(items = [], itemGroupIds) {
  const list = Array.isArray(items) ? [...items] : []
  const groupIds = normalizeTabMenuIds(itemGroupIds)
  if (groupIds.length) {
    const byId = new Map()
    for (const item of list) {
      const key = String(item?.groupId ?? item?.id ?? '')
      if (key && !byId.has(key)) byId.set(key, item)
    }
    const ordered = []
    const used = new Set()
    for (const id of groupIds) {
      const item = byId.get(String(id))
      if (!item) continue
      ordered.push(item)
      used.add(String(item.groupId ?? item.id ?? ''))
    }
    for (const item of list) {
      const key = String(item?.groupId ?? item?.id ?? '')
      if (!used.has(key)) ordered.push(item)
    }
    return ordered
  }
  if (list.some((item) => item?.sortOrder != null && item.sortOrder !== '')) {
    return list.sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
  }
  return list
}

/** 有独立配置用配置；非明确 AUTO 时按选条顺序推标签 */
function resolveFocusCarouselTabOrder(tabs, {
  tabMenuIds,
  items = [],
  itemGroupIds,
  itemMode,
  menus = []
} = {}) {
  const explicit = normalizeTabMenuIds(tabMenuIds)
  if (explicit.length) return applyTabMenuOrder(tabs, explicit)
  const hasSelectionOrder = normalizeTabMenuIds(itemGroupIds).length > 0
  if (itemMode === 'AUTO' && !hasSelectionOrder) return tabs
  const seenIds = []
  const seen = new Set()
  for (const item of orderItemsForTabSequence(items, itemGroupIds)) {
    const tabId = resolveItemTabMenuId(item, tabs, menus)
    if (tabId == null || tabId === '') continue
    const key = String(tabId)
    if (seen.has(key)) continue
    seen.add(key)
    seenIds.push(tabId)
  }
  return applyTabMenuOrder(tabs, seenIds)
}

/** 构建焦点轮播的 Tab 分组及每个 Tab 下的条目 */
function buildFocusCarouselTabs({
  menus = [],
  menuId,
  items = [],
  contentType = 'NEWS',
  displayCount = 0,
  tabMenuIds,
  itemGroupIds,
  itemMode
}) {
  const resolvedMenuId = menuId ?? inferFocusCarouselMenuId(menus, items, contentType)
  const node = findRawMenuNodeById(menus, resolvedMenuId)

  let tabs = []
  if (node) {
    const children = getFocusCarouselTabChildren(node, contentType)
    if (children.length > 1) {
      tabs = buildTabsFromMenuChildren(children, items, displayCount)
    }
  }

  if (tabs.length <= 1) {
    tabs = buildTabsFromItems(menus, items, displayCount)
  }

  if (tabs.length <= 1) {
    return { menuId: resolvedMenuId, tabs: [], items }
  }

  return {
    menuId: resolvedMenuId,
    tabs: resolveFocusCarouselTabOrder(tabs, {
      tabMenuIds,
      items,
      itemGroupIds,
      itemMode,
      menus
    })
  }
}

/** 格式化发布日期为 YYYY-MM-DD（本地时区；优先 publishedAt，缺省用 createdAt） */
function formatPublishedAt(value, fallback) {
  const raw = value || fallback
  if (!raw) return ''
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return String(raw).slice(0, 10)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** 优先使用门户已解析的 summary，其次按 locale 从 summaries map 取值 */
function resolveCmsSummary(section, locale = '') {
  const direct = String(section?.summary || '').trim()
  if (direct) return direct
  const map = section?.summaries
  if (!map || typeof map !== 'object') return ''
  if (locale && map[locale] != null) {
    const byLocale = String(map[locale]).trim()
    if (byLocale) return byLocale
  }
  return ''
}

/** 标准化通用内容条目（含链接与封面） */
function normalizeContentItem(item, contentType, menus = [], extra = {}) {
  if (!item) return null
  const type = item.type || contentType || ''
  const routeKey = String(item.id || item.slug || '')
  const normalized = {
    id: item.id,
    menuId: item.menuId,
    menuName: item.menuName || '',
    groupId: item.groupId ?? null,
    sortOrder: item.sortOrder ?? null,
    categoryId: item.categoryId ?? null,
    slug: routeKey,
    title: item.title || '',
    summary: item.summary || '',
    desc: item.summary || '',
    image: resolveFileUrl(item.coverUrl),
    cover: resolveFileUrl(item.coverUrl),
    publishedAt: formatPublishedAt(item.publishedAt, item.createdAt),
    tags: Array.isArray(item.tags) ? item.tags : [],
    type,
    path: item.path || '',
    ...extra
  }

  // 新闻/方案的 menuId 指向分类菜单，不能写入 path，否则会跳到分类列表而非详情
  if (
    !normalized.path
    && item.menuId
    && menus.length
    && type !== 'NEWS'
    && contentType !== 'NEWS'
    && type !== 'SOLUTION'
    && contentType !== 'SOLUTION'
  ) {
    normalized.path = findMenuPathByMenuId(menus, item.menuId) || ''
  }

  normalized.link = resolveItemLink(normalized, contentType, menus)
  return normalized
}

/** 标准化新闻类内容条目 */
function normalizeNewsItem(item, contentType, menus = []) {
  return normalizeContentItem(item, contentType, menus)
}

/** 焦点轮播：无封面时用正文第一张图 */
function normalizeFocusCarouselItem(item, contentType, menus = []) {
  const normalized = normalizeGenericItem(item, contentType, menus)
  if (!normalized) return null
  if (normalized.cover) return normalized
  const fromBody = extractFirstContentImage(item.bodyHtml, item.bodyJson)
  if (!fromBody) return normalized
  return {
    ...normalized,
    cover: fromBody,
    image: normalized.image || fromBody
  }
}

/** 标准化解决方案类内容条目 */
function normalizeSolutionItem(item, contentType, menus = []) {
  const menuNode = item?.menuId != null ? findRawMenuNodeById(menus, item.menuId) : null
  const navLabel = menuNode?.name || item?.title || ''
  return normalizeContentItem(item, contentType, menus, {
    nav: navLabel,
    text: item?.summary || ''
  })
}

/** 标准化简介类内容条目 */
function normalizeAboutItem(item, contentType, menus = []) {
  return normalizeContentItem(item, contentType, menus, {
    text: item?.summary || ''
  })
}

/** 按内容类型分发标准化逻辑 */
function normalizeGenericItem(item, contentType, menus = []) {
  if (!item) return null
  const type = item.type || contentType || ''
  if (type === 'SOLUTION') return normalizeSolutionItem(item, contentType, menus)
  if (type === 'INTRODUCTION') return normalizeAboutItem(item, contentType, menus)
  return normalizeNewsItem(item, contentType, menus)
}

/** 标准化产品类内容条目 */
function normalizeProductItem(product) {
  const content = product?.content || product || {}
  const normalized = normalizeContentItem(
    {
      ...content,
      type: 'PRODUCT',
      categoryId: content.categoryId
    },
    'PRODUCT',
    [],
    {
      categoryName: product?.category?.name || ''
    }
  )
  if (!normalized) return null
  normalized.certifications = normalizeCertificationList({
    ...content,
    certificationList:
      product?.certificationList
      || content?.certificationList
      || product?.category?.certificationList,
  })
  normalized.link = resolveProductLink(normalized)
  return normalized
}

/** 标准化产品分组（分类 + 产品列表） */
function normalizeProductGroup(group) {
  if (!group) return null
  const backgroundUrl = resolveFileUrl(
    group.backgroundUrl
    || group.categoryBackgroundUrl
    || group.category?.backgroundUrl
    || ''
  )
  return {
    categoryId: group.categoryId,
    categoryName: group.categoryName || '',
    categoryIcon: resolveFileUrl(group.categoryIcon),
    backgroundUrl,
    parentId: group.parentId ?? group.category?.parentId ?? null,
    products: (group.products || []).map(normalizeProductItem).filter(Boolean)
  }
}

/** 时间轴模板按发布日期降序排序 */
function sortTimelineItems(items) {
  return [...items].sort((a, b) => {
    const left = a.publishedAt || ''
    const right = b.publishedAt || ''
    return right.localeCompare(left)
  })
}

/** 根据模板类型标准化区块内的条目列表 */
function normalizeItems(section, template, contentType, menus = []) {
  const items = section.items || []
  const sourceType = section.sourceType || ''
  let normalized
  if (template === 'FOCUS_CAROUSEL_LIST') {
    normalized = items.map((item) => normalizeFocusCarouselItem(item, contentType, menus)).filter(Boolean)
  } else if (template === 'TAB_PANEL_SWITCH') {
    normalized = items.map((item) => normalizeSolutionItem(item, contentType, menus)).filter(Boolean)
  } else if (template === 'INTRO_CARD_GRID') {
    normalized = items.map((item) => normalizeAboutItem(item, contentType, menus)).filter(Boolean)
  } else if (sourceType === 'PRODUCT_CATEGORY' || contentType === 'PRODUCT') {
    normalized = items.map((item) => normalizeProductItem(item)).filter(Boolean)
  } else {
    normalized = items.map((item) => normalizeGenericItem(item, contentType, menus)).filter(Boolean)
  }
  if (template === 'TIMELINE') {
    return sortTimelineItems(normalized)
  }
  return normalized
}

/** 标准化单个首页区块配置 */
export function normalizeHomeSection(section, menus = [], locale = '') {
  if (!section) return null

  const template = resolveTemplate(section, locale)
  const contentType = section.contentType
    || (section.items || []).find((item) => item?.type)?.type
    || ''
  const displayCount = Number(section.displayCount) || 0
  const cmsSummary = resolveCmsSummary(section, locale)
  const heading = resolveHomeSectionHeadingFields(section, locale)
  const base = {
    id: section.id,
    title: section.title || '',
    subtitle: cmsSummary,
    summaryFromCms: Boolean(cmsSummary),
    path: section.path || '',
    menuId: section.menuId ?? null,
    categoryId: section.categoryId ?? null,
    layout: section.layout || 'GRID',
    template,
    sourceType: section.sourceType || '',
    contentType,
    displayCount,
    templateConfig: section.templateConfig || null,
    titleColor: heading.titleColor || '',
    titleFontSize: heading.titleFontSize || '',
    summaryColor: heading.summaryColor || '',
    summaryFontSize: heading.summaryFontSize || '',
    cardsPerRow: heading.cardsPerRow !== '' ? heading.cardsPerRow : (section.cardsPerRow ?? null),
    categoryCardsPerRow: heading.categoryCardsPerRow !== ''
      ? heading.categoryCardsPerRow
      : (section.categoryCardsPerRow ?? null)
  }

  // 产品Groups
  const productGroups = (section.productGroups || [])
    .map(normalizeProductGroup)
    .filter(Boolean)

  const categories = (() => {
    const tops = productGroups.filter((group) => group.parentId == null || group.parentId === '')
    const source = tops.length ? tops : productGroups
    return source.map((group) => ({
      id: group.categoryId,
      name: group.categoryName,
      iconUrl: group.categoryIcon,
      backgroundUrl: group.backgroundUrl || '',
      parentId: group.parentId ?? null
    }))
  })()

  if (template === 'FOCUS_CAROUSEL_LIST') {
    const items = normalizeItems(section, template, contentType, menus)
    const { menuId, tabs } = buildFocusCarouselTabs({
      menus,
      menuId: section.menuId ?? null,
      items,
      contentType: contentType || 'NEWS',
      displayCount,
      tabMenuIds: section.templateConfig?.tabMenuIds,
      itemGroupIds: section.itemGroupIds,
      itemMode: section.itemMode === 'AUTO' ? 'AUTO' : section.itemMode
    })

    return {
      ...base,
      menuId,
      items: tabs.length > 1 ? (tabs[0]?.items || []) : items,
      tabs,
      productGroups,
      categories
    }
  }

  if (section.sourceType === 'PRODUCT_CATEGORY' || productGroups.length) {
    return {
      ...base,
      items: normalizeItems(section, template, contentType, menus),
      productGroups,
      categories
    }
  }

  return {
    ...base,
    items: normalizeItems(section, template, contentType, menus),
    productGroups: [],
    categories: [],
    tabs: []
  }
}

/** 批量标准化首页区块列表 */
export function normalizeHomeSections(sections = [], menus = [], locale = '') {
  return (sections || [])
    .map((section) => normalizeHomeSection(section, menus, locale))
    .filter(Boolean)
}
